/**
 * IDEA STATE ENGINE V23 - ADAPTIVE STUDIO EDITION
 * Guitar creative-constraint generator — anonymous, coin-free, localStorage-backed.
 */

class IdeaStateEngineV22 {
    constructor() {
        // State
        this.intensity = 5;
        this.timerDuration = 60;
        this.timerRemaining = 0;
        this.timerRAF = null;        // requestAnimationFrame handle
        this.timerEndTime = null;    // wall-clock time when timer should finish
        this.isTimerRunning = false;
        this.regenCount = 0;

        // localStorage key
        this.STORAGE_KEY = 'sa-tool-idea-state';

        // Category visibility (all enabled by default)
        this.activeCategories = {
            tone: true,
            technique: true,
            rhythm: true,
            spatial: true,
            theory: true,
            entropy: true
        };

        // Per-category locks (true = don't re-roll this category)
        this.locks = {};

        // Current mission state
        this.currentMission = {
            seed: null,
            intensity: 5,
            name: 'INITIATING...',
            constraints: {}
        };

        // History — array of { seed, name, timestamp, constraints }
        this.history = [];

        // ===== CONSTRAINT DATABASE =====

        this.constraints = {
            // 1. TONE & SIGNAL
            tone: [
                { id: 'tn_neck',       text: 'Neck pickup only',                               weight: 10, tags: ['warm', 'thick'],      conflicts: [] },
                { id: 'tn_bridge',     text: 'Bridge pickup only',                             weight: 10, tags: ['bright', 'biting'],    conflicts: [] },
                { id: 'tn_mid_split',  text: 'Middle or split-coil position',                  weight: 8,  tags: ['glassy', 'clear'],     conflicts: [] },
                { id: 'tn_vol_roll',   text: 'Volume rolled back — clean up the signal',       weight: 7,  tags: ['clean', 'responsive'], conflicts: ['tn_vol_swell'] },
                { id: 'tn_vol_swell',  text: 'Manual volume swells on each phrase',            weight: 5,  tags: ['ambient', 'slow'],     conflicts: ['tn_vol_roll'] },
                { id: 'tn_tone_dark',  text: 'Tone knob all the way down (dark, jazz)',        weight: 5,  tags: ['dark', 'smooth'],      conflicts: ['tn_tone_bright'] },
                { id: 'tn_tone_bright',text: 'Tone knob all the way up (bright, presence)',   weight: 5,  tags: ['cutting', 'sharp'],    conflicts: ['tn_tone_dark'] }
            ],

            // 2. TECHNIQUE
            technique: [
                { id: 'tc_alt',     text: 'Strict alternate picking throughout',               weight: 10, tags: ['mechanical', 'percussive'], conflicts: ['tc_eco', 'tc_legato', 'tc_hybrid', 'tc_finger'] },
                { id: 'tc_eco',     text: 'Economy / sweep picking — follow the strings',     weight: 8,  tags: ['fluid', 'fast'],            conflicts: ['tc_alt', 'tc_finger'] },
                { id: 'tc_legato',  text: 'Legato focus — hammer-ons and pull-offs only',     weight: 8,  tags: ['liquid', 'smooth'],         conflicts: ['tc_alt', 'tc_staccato'] },
                { id: 'tc_hybrid',  text: 'Hybrid picking — pick and fingers together',       weight: 7,  tags: ['complex', 'dynamic'],       conflicts: ['tc_alt', 'tc_finger', 'tc_eco'] },
                { id: 'tc_finger',  text: 'Fingerstyle — no pick at all',                    weight: 6,  tags: ['organic', 'soft'],          conflicts: ['tc_alt', 'tc_eco', 'tc_hybrid'] },
                { id: 'tc_slide',   text: 'Use a slide (or any smooth object)',               weight: 4,  tags: ['gliding', 'vocal'],         conflicts: [] },
                { id: 'tc_staccato',text: 'Staccato — short, muted articulation',            weight: 6,  tags: ['tight', 'punchy'],          conflicts: ['tc_legato', 'tc_sustain'] },
                { id: 'tc_sustain', text: 'Maximize sustain on every note',                  weight: 5,  tags: ['soaring', 'long'],          conflicts: ['tc_staccato'] }
            ],

            // 3. RHYTHM & FEEL
            rhythm: [
                { id: 'rh_straight', text: 'Straight 16th notes — stay on the grid',         weight: 8, tags: ['grid', 'driving'],    conflicts: ['rh_swing', 'rh_triplet'] },
                { id: 'rh_swing',    text: 'Shuffle / swing feel throughout',                 weight: 8, tags: ['bouncy', 'loose'],    conflicts: ['rh_straight'] },
                { id: 'rh_triplet',  text: 'Triplet subdivisions — think in threes',          weight: 7, tags: ['rolling', 'circular'],conflicts: ['rh_straight'] },
                { id: 'rh_behind',   text: 'Play behind the beat — laid back',               weight: 6, tags: ['lazy', 'soulful'],    conflicts: ['rh_ahead'] },
                { id: 'rh_ahead',    text: 'Play ahead of the beat — push the time',         weight: 6, tags: ['rushing', 'urgent'],  conflicts: ['rh_behind'] },
                { id: 'rh_space',    text: 'Leave beat 1 completely empty every bar',         weight: 5, tags: ['syncopated', 'airy'], conflicts: [] },
                { id: 'rh_sync',     text: 'Accent the weak beats — syncopation',            weight: 6, tags: ['funky', 'offbeat'],   conflicts: [] }
            ],

            // 4. SPATIAL
            spatial: [
                { id: 'sp_one_string',  text: 'Single-string focus — stay on one string',   weight: 6, tags: ['linear', 'horizontal'],conflicts: ['sp_string_skip', 'sp_3_oct'] },
                { id: 'sp_string_skip', text: 'Wide string skips — avoid adjacent strings', weight: 6, tags: ['angular', 'vertical'], conflicts: ['sp_one_string'] },
                { id: 'sp_low_frets',   text: 'Lower register — frets 0 through 5',         weight: 7, tags: ['deep', 'heavy'],       conflicts: ['sp_high_frets', 'sp_mid_frets'] },
                { id: 'sp_mid_frets',   text: 'Middle register — frets 5 through 12',       weight: 7, tags: ['vocal', 'centered'],   conflicts: ['sp_low_frets', 'sp_high_frets'] },
                { id: 'sp_high_frets',  text: 'Upper register — frets 12 and above',        weight: 7, tags: ['piercing', 'high'],    conflicts: ['sp_low_frets', 'sp_mid_frets'] },
                { id: 'sp_diag',        text: 'Diagonal movement across the neck',          weight: 5, tags: ['traveling', 'wide'],   conflicts: [] }
            ],

            // 5. THEORY
            theory: [
                { id: 'th_penta',     text: 'Pentatonic scale only — no outside notes',     weight: 10, tags: ['bluesy', 'classic'],   conflicts: ['th_chromatic', 'th_triad'] },
                { id: 'th_triad',     text: 'Triad arpeggios only — outline the chords',    weight: 8,  tags: ['melodic', 'structured'],conflicts: ['th_penta'] },
                { id: 'th_chromatic', text: 'Use chromatic passing tones freely',           weight: 7,  tags: ['jazzy', 'outside'],    conflicts: ['th_penta'] },
                { id: 'th_3rds',      text: 'Move in intervals of 3rds',                   weight: 6,  tags: ['harmonic', 'sweet'],   conflicts: ['th_6ths', 'th_octaves'] },
                { id: 'th_6ths',      text: 'Move in intervals of 6ths',                   weight: 5,  tags: ['open', 'lyrical'],     conflicts: ['th_3rds'] },
                { id: 'th_octaves',   text: 'Octave shapes throughout (Wes-style)',         weight: 6,  tags: ['thick', 'jazz'],       conflicts: [] }
            ],

            // 6. ENTROPY (creative disruptors)
            entropy: [
                { id: 'en_eyes',    text: 'Play with eyes closed',                         weight: 7, tags: ['sensory', 'blind'],     conflicts: [] },
                { id: 'en_breath',  text: 'Coordinate each phrase with your breathing',    weight: 6, tags: ['human', 'organic'],     conflicts: [] },
                { id: 'en_soft',    text: 'Dynamic: very soft (pianissimo) throughout',    weight: 6, tags: ['whisper', 'intimate'],  conflicts: ['en_loud'] },
                { id: 'en_loud',    text: 'Dynamic: very loud (fortissimo) throughout',    weight: 6, tags: ['power', 'aggressive'],  conflicts: ['en_soft'] },
                { id: 'en_vibrato', text: 'Wide, expressive vibrato on every long note',   weight: 6, tags: ['singing', 'wobbly'],    conflicts: ['en_no_vib'] },
                { id: 'en_no_vib',  text: 'No vibrato — straight, classical tone',        weight: 5, tags: ['stoic', 'static'],      conflicts: ['en_vibrato'] },
                { id: 'en_drone',   text: 'Keep the lowest available open string droning',weight: 5, tags: ['pedal', 'ambient'],     conflicts: [] },
                { id: 'en_silence', text: 'Silence counts: leave 2+ beats of space per bar',weight: 6,tags: ['space', 'listening'],  conflicts: [] }
            ]
        };
    }

    // ===== INIT =====
    init() {
        this.loadSettings();
        this.bindEvents();
        this.bindKeyboard();
        this.bindVisibility();
        this.applySettings();
        this.checkUrlSeed();
        if (!this.currentMission.seed) {
            this.generateMission();
        }
        this.renderHistory();
    }

    // ===== SETTINGS PERSISTENCE =====
    loadSettings() {
        try {
            const raw = window.flPreferenceStorage.getItem(this.STORAGE_KEY);
            if (!raw) return;
            const saved = JSON.parse(raw);
            if (saved && typeof saved === 'object') {
                if (typeof saved.intensity === 'number') this.intensity = saved.intensity;
                if (typeof saved.timerDuration === 'number') this.timerDuration = saved.timerDuration;
                if (saved.activeCategories && typeof saved.activeCategories === 'object') {
                    Object.keys(this.activeCategories).forEach(cat => {
                        if (typeof saved.activeCategories[cat] === 'boolean') {
                            this.activeCategories[cat] = saved.activeCategories[cat];
                        }
                    });
                }
                if (Array.isArray(saved.history)) {
                    // Keep last 10
                    this.history = saved.history.slice(0, 10);
                }
            }
        } catch (_) {
            // Corrupt storage — ignore, start fresh
        }
    }

    saveSettings() {
        try {
            window.flPreferenceStorage.setItem(this.STORAGE_KEY, JSON.stringify({
                intensity: this.intensity,
                timerDuration: this.timerDuration,
                activeCategories: this.activeCategories,
                history: this.history.slice(0, 10)
            }));
        } catch (_) { /* quota exceeded — non-fatal */ }
    }

    applySettings() {
        // Intensity slider
        const slider = document.getElementById('IntensitySlider');
        const val = document.getElementById('IntensityValue');
        if (slider) { slider.value = this.intensity; }
        if (val)    { val.textContent = this.intensity; }

        // Timer preset active state
        document.querySelectorAll('.timer-preset').forEach(btn => {
            const active = parseInt(btn.dataset.time) === this.timerDuration;
            btn.classList.toggle('active', active);
            btn.setAttribute('aria-pressed', String(active));
        });

        // Reset timer display to match loaded duration
        this._setTimerDisplay(this.timerDuration);

        // Category checkboxes
        this.updateConfigUI();
    }

    // ===== SYNERGY WEIGHTING =====
    getSynergyMultiplier(constraint, selections) {
        let multiplier = 1.0;
        const tags = constraint.tags || [];

        const vibes = {
            ambient:     ['swell', 'delay', 'space', 'slow', 'liquid', 'horizontal', 'pedal', 'listening'],
            aggressive:  ['bridge', 'bright', 'staccato', 'tight', 'driving', 'power'],
            jazz:        ['neck', 'dark', 'warm', 'swing', 'triplet', 'chromatic'],
            blues:       ['neck', 'warm', 'pentatonic', 'bluesy', 'shuffle', 'slide']
        };

        selections.forEach(sel => {
            const selTags = sel.tags || [];
            Object.keys(vibes).forEach(vibe => {
                const vibeKeywords = vibes[vibe];
                if (selTags.some(t => vibeKeywords.includes(t))) {
                    if (tags.some(t => vibeKeywords.includes(t))) {
                        multiplier += 0.5;
                    }
                }
            });
        });

        return multiplier;
    }

    // ===== WEIGHTED RANDOMIZATION =====
    weightedRandom(constraints, existingSelections = []) {
        const valid = constraints.filter(c => {
            return !existingSelections.some(e =>
                (c.conflicts && c.conflicts.includes(e.id)) ||
                (e.conflicts && e.conflicts.includes(c.id))
            );
        });

        if (valid.length === 0) return null;

        const candidates = valid.map(c => {
            let w = c.weight;
            if (this.intensity > 7) w = Math.max(1, w * 0.5 + 2);
            w = w * this.getSynergyMultiplier(c, existingSelections);
            return { ...c, _w: w };
        });

        let total = candidates.reduce((s, c) => s + c._w, 0);
        let r = Math.random() * total;
        for (const c of candidates) {
            r -= c._w;
            if (r <= 0) return c;
        }
        return candidates[candidates.length - 1];
    }

    // ===== MISSION GENERATION =====
    generateMission(seedOverride = null) {
        if (seedOverride) {
            this.loadFromSeed(seedOverride);
            return;
        }

        let globalSelections = [];
        let newConstraints = {};
        const categories = ['tone', 'technique', 'rhythm', 'spatial', 'theory', 'entropy'];

        // 1. Locked items stay
        categories.forEach(cat => {
            if (this.locks[cat] && this.currentMission.constraints[cat]) {
                newConstraints[cat] = this.currentMission.constraints[cat];
                globalSelections.push(this.currentMission.constraints[cat]);
            }
        });

        // 2. Generate new items for unlocked categories
        categories.forEach(cat => {
            if (this.locks[cat]) return;
            if (!this.activeCategories[cat]) { newConstraints[cat] = null; return; }

            // Low-intensity density reduction
            if (this.intensity < 3 && Math.random() > 0.7 && ['entropy', 'spatial'].includes(cat)) {
                newConstraints[cat] = null;
                return;
            }

            const selection = this.weightedRandom(this.constraints[cat], globalSelections);
            if (selection) {
                newConstraints[cat] = selection;
                globalSelections.push(selection);
            }
        });

        this.currentMission.constraints = newConstraints;
        this.currentMission.seed       = this.generateSeed();
        this.currentMission.intensity  = this.intensity;
        this.currentMission.name       = this.generateMissionName(globalSelections);

        // Save to history
        this._pushHistory({
            seed:        this.currentMission.seed,
            name:        this.currentMission.name,
            timestamp:   Date.now(),
            constraints: newConstraints
        });

        this.regenCount++;
        this.renderMission();
        this.saveSettings();

        this._announce(`Prompt ${this.currentMission.seed}: ${this.currentMission.name}`);
    }

    _pushHistory(entry) {
        // Remove any existing entry with same seed (avoid duplicates if re-loading)
        this.history = this.history.filter(h => h.seed !== entry.seed);
        this.history.unshift(entry);
        if (this.history.length > 10) this.history.length = 10;
    }

    generateMissionName(selections) {
        if (!selections || selections.length === 0) return 'Free Play';

        const adjectives = [];
        const nouns = [];

        selections.forEach(s => {
            (s.tags || []).forEach(t => {
                const adjectiveTags = ['warm', 'bright', 'dark', 'clean', 'dirty', 'heavy', 'soft', 'liquid', 'tight', 'loose', 'lazy', 'driving', 'human', 'blind', 'angular', 'fluid', 'soaring'];
                if (adjectiveTags.includes(t)) adjectives.push(t.charAt(0).toUpperCase() + t.slice(1));

                const nounMap = {
                    percussive: 'Percussion', fluid: 'Flow', angular: 'Angles', linear: 'Line',
                    circular: 'Cycles', vertical: 'Ascension', horizontal: 'Horizon',
                    bluesy: 'Blues', jazzy: 'Jazz', organic: 'Life', mechanical: 'Machine',
                    whisper: 'Whispers', power: 'Power', pedal: 'Drone', listening: 'Silence'
                };
                if (nounMap[t]) nouns.push(nounMap[t]);
            });
        });

        if (adjectives.length === 0) adjectives.push('Abstract');
        if (nouns.length === 0) nouns.push('Practice');

        const adj  = adjectives[Math.floor(Math.random() * adjectives.length)];
        const noun = nouns[Math.floor(Math.random() * nouns.length)];
        return `${adj} ${noun}`;
    }

    generateSeed() {
        const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
        let seed = '';
        for (let i = 0; i < 6; i++) seed += chars[Math.floor(Math.random() * chars.length)];
        return seed;
    }

    loadFromSeed(seed) {
        seed = seed.trim().toUpperCase().slice(0, 6);
        let seedVal = 0;
        for (let i = 0; i < seed.length; i++) seedVal += seed.charCodeAt(i);

        const seededRandom = () => {
            const x = Math.sin(seedVal++) * 10000;
            return x - Math.floor(x);
        };

        const categories = ['tone', 'technique', 'rhythm', 'spatial', 'theory', 'entropy'];
        let selections = [];
        let newConstraints = {};

        categories.forEach(cat => {
            const pool = this.constraints[cat];
            const idx  = Math.floor(seededRandom() * pool.length);
            newConstraints[cat] = pool[idx];
            selections.push(pool[idx]);
        });

        this.currentMission.seed        = seed;
        this.currentMission.constraints = newConstraints;
        this.currentMission.name        = this.generateMissionName(selections);

        this._pushHistory({
            seed:        seed,
            name:        this.currentMission.name,
            timestamp:   Date.now(),
            constraints: newConstraints
        });

        this.renderMission();
        this.saveSettings();
        this._announce(`Loaded prompt ${seed}: ${this.currentMission.name}`);
    }

    // ===== RENDER MISSION =====
    renderMission() {
        const missionId = document.getElementById('MissionId');
        if (missionId) missionId.textContent = `#${this.currentMission.seed}`;

        const label = document.querySelector('.mission-label');
        if (label) {
            label.textContent = 'PROMPT: ';
            const name = document.createElement('span');
            name.textContent = this.currentMission.name;
            label.appendChild(name);
        }

        const container = document.getElementById('MissionGrid');
        if (!container) return;
        container.innerHTML = '';

        const categories = ['tone', 'technique', 'rhythm', 'spatial', 'theory', 'entropy'];

        categories.forEach(cat => {
            const item = this.currentMission.constraints[cat];
            if (!item && !this.activeCategories[cat]) return;

            const card = document.createElement('div');
            card.className = `mission-card-item${!item ? ' inactive' : ''}${cat === 'entropy' ? ' is-entropy' : ''}`;

            const header = document.createElement('div');
            header.className = 'card-header';

            const title = document.createElement('span');
            title.className = 'card-title';
            title.textContent = cat.toUpperCase();

            const lockBtn = document.createElement('button');
            lockBtn.className  = `card-lock${this.locks[cat] ? ' locked' : ''}`;
            lockBtn.textContent = this.locks[cat] ? 'LOCKED' : 'LOCK';
            lockBtn.title      = this.locks[cat] ? 'Unlock this category' : 'Lock this category';
            lockBtn.setAttribute('aria-label', `${this.locks[cat] ? 'Unlock' : 'Lock'} ${cat} category`);
            lockBtn.setAttribute('aria-pressed', String(!!this.locks[cat]));
            lockBtn.onclick    = () => this.toggleLock(cat, lockBtn);

            header.appendChild(title);
            header.appendChild(lockBtn);

            const body = document.createElement('div');
            body.className = 'card-body';

            if (item) {
                body.innerHTML = this._highlight(item.text);
            } else {
                body.innerHTML = '<span style="opacity:0.4">Disabled</span>';
            }

            card.appendChild(header);
            card.appendChild(body);
            container.appendChild(card);
        });

        this.renderHistory();
    }

    toggleLock(cat, btn) {
        this.locks[cat] = !this.locks[cat];
        btn.classList.toggle('locked', this.locks[cat]);
        btn.setAttribute('aria-pressed', String(this.locks[cat]));
        btn.title = this.locks[cat] ? 'Unlock this category' : 'Lock this category';
        btn.textContent = this.locks[cat] ? 'LOCKED' : 'LOCK';
        btn.setAttribute('aria-label', `${this.locks[cat] ? 'Unlock' : 'Lock'} ${cat} category`);
    }

    _highlight(text) {
        const kw = ['only', 'every', 'no ', 'strict', 'neck', 'bridge', 'volume', 'picking', 'legato', 'staccato', 'triplets', 'swing', 'frets', 'eyes closed', 'fingerstyle', 'slide', 'all the way'];
        let result = text;
        kw.forEach(k => {
            const re = new RegExp(`(${k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
            result = result.replace(re, '<strong>$1</strong>');
        });
        return result;
    }

    // ===== HISTORY =====
    renderHistory() {
        const list = document.getElementById('HistoryList');
        if (!list) return;

        if (this.history.length === 0) {
            const empty = document.createElement('li');
            empty.className = 'history-empty';
            empty.textContent = 'No prompts yet — choose New prompt.';
            list.replaceChildren(empty);
            return;
        }

        list.replaceChildren();
        this.history.forEach(entry => {
            const li = document.createElement('li');
            li.className = 'history-list-item';
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'history-item';
            button.setAttribute('aria-label', `Load prompt ${entry.seed}: ${entry.name}`);
            button.title = 'Load this prompt';

            const ago = this._timeAgo(entry.timestamp);
            const id = document.createElement('span');
            id.className = 'history-item-id';
            id.textContent = `#${entry.seed}`;
            const name = document.createElement('span');
            name.className = 'history-item-name';
            name.textContent = entry.name;
            const time = document.createElement('span');
            time.className = 'history-item-time';
            time.textContent = ago;
            button.append(id, name, time);

            const load = () => {
                // Restore the exact constraints from history
                this.currentMission.seed        = entry.seed;
                this.currentMission.name        = entry.name;
                this.currentMission.constraints = entry.constraints;
                const inp = document.getElementById('SeedInput'); if (inp) inp.value = entry.seed;
                this.renderMission();
                this._announce(`Loaded prompt ${entry.seed}: ${entry.name}`);
            };
            button.addEventListener('click', load);

            li.appendChild(button);
            list.appendChild(li);
        });
    }

    _timeAgo(ts) {
        const diff = Date.now() - ts;
        const mins = Math.floor(diff / 60000);
        if (mins < 1)   return 'just now';
        if (mins < 60)  return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24)   return `${hrs}h ago`;
        return `${Math.floor(hrs / 24)}d ago`;
    }

    exportAll() {
        if (this.history.length === 0) { this.showToast('No prompts to copy.'); return; }

        const lines = ['IDEA STATE — Prompt History', '='.repeat(40)];
        this.history.forEach((entry, i) => {
            lines.push('');
            lines.push(`#${entry.seed}  ${entry.name}`);
            lines.push('-'.repeat(30));
            Object.keys(entry.constraints || {}).forEach(cat => {
                const item = entry.constraints[cat];
                if (item) lines.push(`${cat.toUpperCase()}: ${item.text}`);
            });
        });
        lines.push('');
        lines.push(`Exported ${new Date().toLocaleDateString()}`);

        this._copyText(lines.join('\n'), 'All prompts copied.');
    }

    // ===== TIMER (RAF-based, not setInterval) =====
    startTimer() {
        if (this.isTimerRunning) { this.stopTimer(); return; }

        this.isTimerRunning = true;
        this.timerEndTime   = Date.now() + this.timerDuration * 1000;

        const btn = document.getElementById('TimerStart');
        const display = document.getElementById('TimerDisplay');
        if (btn)     { btn.textContent = 'Stop'; btn.classList.add('running'); btn.setAttribute('aria-label', 'Stop prompt timer'); btn.setAttribute('aria-pressed', 'true'); }
        if (display) { display.classList.add('running'); display.classList.remove('done'); }

        const tick = () => {
            if (!this.isTimerRunning) return;
            const remaining = Math.ceil((this.timerEndTime - Date.now()) / 1000);
            if (remaining <= 0) {
                this.timerComplete();
                return;
            }
            this._setTimerDisplay(remaining);
            this.timerRAF = requestAnimationFrame(tick);
        };
        this.timerRAF = requestAnimationFrame(tick);
    }

    stopTimer() {
        this.isTimerRunning = false;
        if (this.timerRAF) { cancelAnimationFrame(this.timerRAF); this.timerRAF = null; }

        const btn     = document.getElementById('TimerStart');
        const display = document.getElementById('TimerDisplay');
        if (btn)     { btn.textContent = 'Start'; btn.classList.remove('running'); btn.setAttribute('aria-label', 'Start prompt timer'); btn.setAttribute('aria-pressed', 'false'); }
        if (display) { display.classList.remove('running', 'done'); }
        this._setTimerDisplay(this.timerDuration);
    }

    timerComplete() {
        this.isTimerRunning = false;
        if (this.timerRAF) { cancelAnimationFrame(this.timerRAF); this.timerRAF = null; }

        const btn     = document.getElementById('TimerStart');
        const display = document.getElementById('TimerDisplay');
        if (btn)     { btn.textContent = 'Start'; btn.classList.remove('running'); btn.setAttribute('aria-label', 'Start prompt timer'); btn.setAttribute('aria-pressed', 'false'); }
        if (display) { display.classList.remove('running'); display.classList.add('done'); display.textContent = 'DONE'; }
        this._announce('Timer complete.');
    }

    _setTimerDisplay(seconds) {
        const el = document.getElementById('TimerDisplay');
        if (!el) return;
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        el.textContent = `${m}:${s.toString().padStart(2, '0')}`;
    }

    // ===== COPY / SHARE =====
    copyMission() {
        const lines = [
            `IDEA STATE PROMPT #${this.currentMission.seed}`,
            `Prompt: ${this.currentMission.name.toUpperCase()}`,
            '—'.repeat(24)
        ];
        Object.keys(this.currentMission.constraints).forEach(cat => {
            const item = this.currentMission.constraints[cat];
            if (item) lines.push(`${cat.toUpperCase()}: ${item.text}`);
        });
        lines.push('—'.repeat(24));
        lines.push(`Load it: ${window.location.origin}${window.location.pathname}?seed=${this.currentMission.seed}`);

        this._copyText(lines.join('\n'), 'Prompt copied.');
    }

    shareLink() {
        const url = `${window.location.origin}${window.location.pathname}?seed=${this.currentMission.seed}`;
        this._copyText(url, 'Link copied.');
    }

    _copyText(text, successMessage) {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text)
                .then(() => this.showToast(successMessage))
                .catch(() => window.prompt('Copy this text:', text));
            return;
        }
        window.prompt('Copy this text:', text);
    }

    checkUrlSeed() {
        const params = new URLSearchParams(window.location.search);
        const seed   = params.get('seed');
        if (seed) {
            const input = document.getElementById('SeedInput');
            if (input) input.value = seed;
            this.generateMission(seed);
        }
    }

    // ===== ACCESSIBILITY =====
    _announce(msg) {
        const el = document.getElementById('IdeaStatus');
        if (!el) return;
        el.textContent = '';
        // Tiny delay ensures screen readers pick up the change
        requestAnimationFrame(() => { el.textContent = msg; });
    }

    // ===== TOAST =====
    showToast(msg) {
        const t = document.createElement('div');
        t.className = 'idea-toast';
        t.setAttribute('role', 'status');
        t.textContent = msg;
        document.body.appendChild(t);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => { t.classList.add('show'); });
        });
        setTimeout(() => {
            t.classList.remove('show');
            setTimeout(() => t.remove(), 300);
        }, 2200);
    }

    updateConfigUI() {
        Object.keys(this.activeCategories).forEach(cat => {
            const cb = document.getElementById(`cfg_${cat}`);
            if (cb) cb.checked = this.activeCategories[cat];
        });
    }

    // ===== EVENT BINDING =====
    bindEvents() {
        document.getElementById('GenerateBtn')?.addEventListener('click', () => this.generateMission());

        // Intensity slider
        const slider = document.getElementById('IntensitySlider');
        const val    = document.getElementById('IntensityValue');
        if (slider) {
            slider.addEventListener('input', () => {
                this.intensity = parseInt(slider.value);
                if (val) val.textContent = this.intensity;
            });
            slider.addEventListener('change', () => this.saveSettings());
        }

        // Settings dialog
        const settingsButton = document.getElementById('SettingsBtn');
        const settingsModal = document.getElementById('SettingsModal');
        const settingsClose = document.getElementById('CloseSettings');
        let settingsReturnFocus = settingsButton;
        if (settingsModal && settingsModal.parentElement !== document.body) document.body.appendChild(settingsModal);
        const closeSettings = () => {
            if (!settingsModal) return;
            window.FLToolDialogIsolation?.close(settingsModal);
            settingsModal.classList.remove('active');
            settingsModal.hidden = true;
            settingsModal.setAttribute('aria-hidden', 'true');
            settingsModal.setAttribute('inert', '');
            settingsButton?.setAttribute('aria-expanded', 'false');
            if (settingsReturnFocus && document.contains(settingsReturnFocus)) settingsReturnFocus.focus();
        };
        const openSettings = () => {
            if (!settingsModal) return;
            settingsReturnFocus = document.activeElement;
            settingsModal.hidden = false;
            settingsModal.removeAttribute('inert');
            settingsModal.setAttribute('aria-hidden', 'false');
            settingsModal.classList.add('active');
            settingsButton?.setAttribute('aria-expanded', 'true');
            window.FLToolDialogIsolation?.open(settingsModal);
            settingsClose?.focus();
        };
        settingsButton?.addEventListener('click', openSettings);
        settingsClose?.addEventListener('click', closeSettings);
        // Click outside modal to close
        document.getElementById('SettingsModal')?.addEventListener('click', e => {
            if (e.target === e.currentTarget) {
                closeSettings();
            }
        });
        settingsModal?.addEventListener('keydown', e => {
            if (e.key === 'Escape') { e.preventDefault(); closeSettings(); return; }
            if (e.key !== 'Tab') return;
            const items = Array.from(settingsModal.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'));
            if (!items.length) return;
            const first = items[0];
            const last = items[items.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        });

        // Category checkboxes
        document.querySelectorAll('.cfg-checkbox').forEach(cb => {
            cb.addEventListener('change', e => {
                const cat = e.target.dataset.cat;
                this.activeCategories[cat] = e.target.checked;
                this.saveSettings();
            });
        });

        // Timer presets
        document.querySelectorAll('.timer-preset').forEach(btn => {
            btn.addEventListener('click', () => {
                document.querySelectorAll('.timer-preset').forEach(b => {
                    b.classList.remove('active');
                    b.setAttribute('aria-pressed', 'false');
                });
                btn.classList.add('active');
                btn.setAttribute('aria-pressed', 'true');
                this.timerDuration = parseInt(btn.dataset.time);
                this.stopTimer();
                this.saveSettings();
            });
        });
        document.getElementById('TimerStart')?.addEventListener('click', () => this.startTimer());

        // Copy / Share
        document.getElementById('CopyBtn')?.addEventListener('click', () => this.copyMission());
        document.getElementById('ShareBtn')?.addEventListener('click', () => this.shareLink());

        // Load seed
        document.getElementById('LoadSeedBtn')?.addEventListener('click', () => {
            const s = document.getElementById('SeedInput')?.value.trim();
            if (s) this.generateMission(s);
        });
        document.getElementById('SeedInput')?.addEventListener('keydown', e => {
            if (e.key === 'Enter') {
                const s = e.target.value.trim();
                if (s) this.generateMission(s);
            }
        });

        // Export history
        document.getElementById('ExportAllBtn')?.addEventListener('click', () => this.exportAll());
    }

    bindKeyboard() {
        document.addEventListener('keydown', e => {
            const active = document.activeElement;
            const isInteractive = active?.closest?.('a, button, input, textarea, select, summary, [contenteditable="true"], [role="dialog"]');
            if (e.key === ' ' && !isInteractive) {
                e.preventDefault();
                this.startTimer();
            }
        });
    }

    bindVisibility() {
        // Stop timer if tab is hidden (no audio in this tool but follows the pattern)
        document.addEventListener('visibilitychange', () => {
            if (document.hidden && this.isTimerRunning) {
                this.stopTimer();
            }
        });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    window.flRunWhenPrivacyReady(() => {
        window.ideaApp = new IdeaStateEngineV22();
        window.ideaApp.init();
    });
});
